import React, { useRef, useCallback, useState, useMemo, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR, COLORS, COMMUNITY_COLORS, COMMUNITY_STROKE_COLORS, getDepartmentColorMap } from './graph/graphConfig';
import { useGraphInteraction } from './graph/useGraphInteraction';
import { useAdaptiveRendering } from './graph/useAdaptiveRendering';
import { renderNode, getNodeRadius } from './graph/nodeRenderers';
import { getCollaborationLinkColor, getCollaborationLinkWidth } from './graph/linkRenderers';
import { convexHull } from './graph/graphUtils';
import GraphTooltip from './graph/GraphTooltip';
import GraphLegend from './graph/GraphLegend';
import GraphStatsFooter from './graph/GraphStatsFooter';
import FullscreenButton from './graph/FullscreenButton';
import type { FacultyGraphResponse, FacultyPersonNode, FacultyPersonEdge } from '../types/api';

const NODE_RADIUS_BASE = 4;
const NODE_RADIUS_MULTIPLIER = 1.0;
const LABEL_FONT_SIZE = 8;
const SHOW_LABELS_IN_OVERVIEW = true;

interface FacultyGraphNode {
  id: number;
  name: string;
  department?: string;
  projects_count?: number;
  x?: number;
  y?: number;
  fx?: number;
  fy?: number;
}

interface FacultyGraphLink {
  source: number;
  target: number;
  sharedProjects: number;
}

interface FacultyGraphViewProps {
  data: FacultyGraphResponse;
  onPersonClick: (personId: number) => void;
  onBack: () => void;
}

export default function FacultyGraphView({ data, onPersonClick, onBack }: FacultyGraphViewProps) {
  const { t } = useTranslation('network');
  const graphRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hullsDrawnThisFrameRef = useRef<boolean>(false);
  const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(new Set());

  const nodeColor = FACULTY_COLORS[data.abbreviation] || DEFAULT_NODE_COLOR;
  const nodeCount = data.nodes?.length || 0;

  const deptColorMap = useMemo(
    () => getDepartmentColorMap(data.departments || []),
    [data.departments]
  );

  // Build nodes — always pin at backend-computed positions
  const graphNodes = useMemo(() => {
    return data.nodes.map((node: FacultyPersonNode) => {
      const n: FacultyGraphNode = {
        id: node.id,
        name: node.name,
        department: node.department,
        projects_count: node.projects_count,
      };
      if (node.x != null && node.y != null) {
        n.x = node.x;
        n.y = node.y;
        n.fx = node.x;
        n.fy = node.y;
      }
      return n;
    });
  }, [data.nodes]);

  // Raw edges sorted by weight
  const rawEdges = useMemo(() => {
    const nodeIds = new Set(graphNodes.map(n => n.id));
    const edges: FacultyGraphLink[] = data.edges
      .filter((edge: FacultyPersonEdge) => nodeIds.has(edge.source) && nodeIds.has(edge.target))
      .map((edge: FacultyPersonEdge) => ({
        source: edge.source,
        target: edge.target,
        sharedProjects: edge.sharedProjects,
      }));
    edges.sort((a, b) => (b.sharedProjects || 0) - (a.sharedProjects || 0));
    return edges;
  }, [data.edges, graphNodes]);

  const maxSharedProjects = useMemo(() =>
    rawEdges.reduce((max, e) => Math.max(max, e.sharedProjects || 1), 1),
  [rawEdges]);

  // Default threshold: low absolute values to show overall graph structure.
  // Edge color/width scaling handles visual hierarchy — no need to hide edges.
  const defaultMinWeight = useMemo(() => {
    if (rawEdges.length > 5000) return 5;
    if (rawEdges.length > 2000) return 3;
    if (rawEdges.length > 500) return 2;
    return 1;
  }, [rawEdges.length]);

  const [minEdgeWeight, setMinEdgeWeight] = useState<number>(defaultMinWeight);

  // Build filtered graphData with fresh link objects
  const graphData = useMemo(() => {
    const links = rawEdges
      .filter(e => e.sharedProjects >= minEdgeWeight)
      .map(e => ({ source: e.source, target: e.target, sharedProjects: e.sharedProjects }));
    return { nodes: graphNodes, links };
  }, [graphNodes, rawEdges, minEdgeWeight]);

  const interaction = useGraphInteraction(containerRef, graphData.links);
  const adaptive = useAdaptiveRendering(graphRef);

  useEffect(() => {
    adaptive.setContext(graphData.nodes, interaction.adjacencyMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData.nodes, interaction.adjacencyMap, adaptive.setContext]);

  // Minimal physics — nodes are pinned at backend positions; forces only matter during drag
  useEffect(() => {
    const fg = graphRef.current;
    if (!fg) return;
    fg.d3Force('charge').strength(-30);
    fg.d3Force('link').distance(80).strength(0.1);
  }, []);

  const handleNodeClick = useCallback((node: any) => {
    if (node && node.id) onPersonClick(node.id);
  }, [onPersonClick]);

  const handleNodeDragEnd = useCallback((node: any) => {
    node.fx = node.x;
    node.fy = node.y;
  }, []);

  // Zoom-to-fit after simulation settles
  const hasZoomedRef = useRef<boolean>(false);
  useEffect(() => { hasZoomedRef.current = false; }, [nodeCount]);
  const handleEngineStop = useCallback(() => {
    if (hasZoomedRef.current) return;
    hasZoomedRef.current = true;
    graphRef.current?.zoomToFit(400, 60);
  }, []);

  // Reheat on slider change
  const isFirstRender = useRef<boolean>(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const fg = graphRef.current;
    if (!fg) return;
    hasZoomedRef.current = false;
    fg.d3ReheatSimulation();
  }, [minEdgeWeight]);

  // Department hulls — skip for large graphs where hulls overlap into visual noise
  const drawDepartmentHulls = useCallback((ctx: CanvasRenderingContext2D) => {
    if (nodeCount > 300) return;
    if (selectedDepartments.size > 0) return;

    const groups: Record<string, Array<{ x: number; y: number }>> = {};
    for (const node of graphData.nodes) {
      if (node.x == null || node.y == null || !node.department) continue;
      if (!groups[node.department]) groups[node.department] = [];
      groups[node.department].push({ x: node.x, y: node.y });
    }

    const departments = data.departments || [];
    for (const [dept, pts] of Object.entries(groups)) {
      if (pts.length < 3) continue;
      const hull = convexHull(pts);
      if (hull.length < 3) continue;

      const deptIdx = departments.indexOf(dept);
      const colorIdx = (deptIdx >= 0 ? deptIdx : 0) % COMMUNITY_COLORS.length;

      ctx.save();
      ctx.globalAlpha = 1;
      ctx.fillStyle = COMMUNITY_COLORS[colorIdx];

      const pad = 25;
      const cx = pts.reduce((s, q) => s + q.x, 0) / pts.length;
      const cy = pts.reduce((s, q) => s + q.y, 0) / pts.length;
      const expanded = hull.map(p => {
        const dx = p.x - cx;
        const dy = p.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        return { x: p.x + (dx / dist) * pad, y: p.y + (dy / dist) * pad };
      });

      ctx.beginPath();
      ctx.moveTo(expanded[0].x, expanded[0].y);
      for (let i = 1; i < expanded.length; i++) {
        ctx.lineTo(expanded[i].x, expanded[i].y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = COMMUNITY_STROKE_COLORS[colorIdx];
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }
  }, [graphData.nodes, data.departments, selectedDepartments.size, nodeCount]);

  const departments = data.departments || [];

  return (
    <div ref={wrapperRef} className="flex-1 flex flex-col relative overflow-hidden bg-muted/30">
      {/* Header */}
      <div className="px-8 py-4 flex justify-between items-center border-b border-border bg-background">
        <div>
          <h2 className="m-0 text-xl font-bold text-foreground">
            {t('faculty.title')}: {data.abbreviation}
            <span className="font-normal text-base ml-2 text-muted-foreground">
              ({data.facultyName})
            </span>
          </h2>
          <div className="text-sm mt-1 italic text-muted-foreground">
            {t('faculty.clickPerson')}
          </div>
        </div>
        <Button variant="outline" onClick={onBack}>
          {t('faculty.back')}
        </Button>
      </div>

      <GraphStatsFooter stats={[
        { label: t('stats.people'), value: data.stats.totalPeople },
        { label: t('stats.connections'), value: `${graphData.links.length} / ${rawEdges.length}` },
      ]} />

      <div
        ref={containerRef}
        onMouseMove={interaction.handleMouseMove}
        className="flex-1 relative bg-muted/30"
      >
        <FullscreenButton targetRef={wrapperRef} />

        <GraphLegend
          title={t('legend.departments')}
          items={departments.map(dept => ({
            key: dept,
            label: dept,
            color: deptColorMap[dept] || nodeColor,
          }))}
          onItemClick={(key: string) => {
            setSelectedDepartments(prev => {
              const next = new Set(prev);
              if (next.has(key)) next.delete(key);
              else next.add(key);
              return next;
            });
          }}
          selectedItems={selectedDepartments}
          minEdgeValue={minEdgeWeight}
          maxEdgeValue={maxSharedProjects}
          onMinEdgeValueChange={setMinEdgeWeight}
          filterLabel={t('filter.minSharedOutcomes')}
          t={t}
        />

        <ForceGraph2D
          ref={graphRef}
          graphData={graphData}
          nodeId="id"
          nodeVal={(node: any) => getNodeRadius(node.projects_count || 1, NODE_RADIUS_BASE, NODE_RADIUS_MULTIPLIER) / 3}
          nodeCanvasObject={(node: any, ctx: CanvasRenderingContext2D) => {
            // Department hulls — draw once per frame before first node
            if (!hullsDrawnThisFrameRef.current) {
              hullsDrawnThisFrameRef.current = true;
              drawDepartmentHulls(ctx);
              requestAnimationFrame(() => { hullsDrawnThisFrameRef.current = false; });
            }

            const radius = getNodeRadius(node.projects_count || 1, NODE_RADIUS_BASE, NODE_RADIUS_MULTIPLIER);
            const deptFiltered = selectedDepartments.size > 0 && !selectedDepartments.has(node.department);
            if (deptFiltered) {
              if (node.x == null || node.y == null) return;
              ctx.save();
              ctx.globalAlpha = 0.06;
              ctx.beginPath();
              ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
              ctx.fillStyle = deptColorMap[node.department] || nodeColor;
              ctx.fill();
              ctx.restore();
              return;
            }
            const highlighted = interaction.isNodeHighlighted(node.id);
            renderNode(node, ctx, {
              lodTier: adaptive.lodTier,
              isHighlighted: highlighted,
              hasHover: interaction.hasHover,
              isHovered: interaction.hoveredNode?.id === node.id,
              visibleLabels: adaptive.getVisibleLabels(),
              color: deptColorMap[node.department] || nodeColor,
              radius,
              label: node.name,
              bbox: adaptive.getBBox(),
              fontSize: LABEL_FONT_SIZE,
              showLabelsInOverview: SHOW_LABELS_IN_OVERVIEW,
            });
          }}
          linkWidth={(link: any) => {
            if (interaction.hoveredLink === link) {
              const ceiling = Math.max(maxSharedProjects, 15);
              const tw = Math.min((link.sharedProjects || 1) / ceiling, 1);
              return (1.0 + tw * 8.5) * 2;
            }
            return getCollaborationLinkWidth(link, 'sharedProjects', maxSharedProjects, interaction.isLinkHighlighted(link), interaction.hasHover);
          }}
          linkColor={(link: any) => {
            if (interaction.hoveredLink === link) {
              const src = typeof link.source === 'object' ? link.source : null;
              return src ? (deptColorMap[src.department] || nodeColor) : nodeColor;
            }
            const highlighted = interaction.isLinkHighlighted(link);
            const hoveredId = interaction.hoveredNode?.id;
            let edgeColor: string | undefined;
            if (hoveredId && highlighted) {
              const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
              const otherNode = sourceId === hoveredId ? link.target : link.source;
              edgeColor = typeof otherNode === 'object' ? (deptColorMap[otherNode.department] || nodeColor) : nodeColor;
            }
            return getCollaborationLinkColor(link, 'sharedProjects', maxSharedProjects, highlighted, interaction.hasHover, edgeColor);
          }}
          linkCurvature={0.1}
          linkCanvasObjectMode="after"
          linkCanvasObject={(link: any, ctx: CanvasRenderingContext2D) => {
            if (interaction.hoveredLink !== link) return;
            const src = typeof link.source === 'object' ? link.source : null;
            const tgt = typeof link.target === 'object' ? link.target : null;
            if (!src || !tgt || src.x == null || tgt.x == null) return;

            const mx = (src.x + tgt.x) / 2;
            const my = (src.y + tgt.y) / 2;
            const label = `${link.sharedProjects}`;

            ctx.save();
            ctx.font = 'bold 5px Sans-Serif';
            const textW = ctx.measureText(label).width;
            const padX = 3;
            const padY = 2;
            const bx = mx - textW / 2 - padX;
            const by = my - 4 - padY;
            const bw = textW + padX * 2;
            const bh = 8 + padY * 2;
            const rr = 3;
            ctx.fillStyle = 'rgba(30, 41, 59, 0.85)';
            ctx.beginPath();
            ctx.moveTo(bx + rr, by);
            ctx.lineTo(bx + bw - rr, by);
            ctx.arcTo(bx + bw, by, bx + bw, by + rr, rr);
            ctx.lineTo(bx + bw, by + bh - rr);
            ctx.arcTo(bx + bw, by + bh, bx + bw - rr, by + bh, rr);
            ctx.lineTo(bx + rr, by + bh);
            ctx.arcTo(bx, by + bh, bx, by + bh - rr, rr);
            ctx.lineTo(bx, by + rr);
            ctx.arcTo(bx, by, bx + rr, by, rr);
            ctx.closePath();
            ctx.fill();

            ctx.fillStyle = '#fff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, mx, my);
            ctx.restore();
          }}
          linkVisibility={(link: any) => {
            if (selectedDepartments.size > 0) {
              const srcDept = typeof link.source === 'object' ? link.source.department : null;
              const tgtDept = typeof link.target === 'object' ? link.target.department : null;
              if (!selectedDepartments.has(srcDept) || !selectedDepartments.has(tgtDept)) return false;
            }
            const highlighted = interaction.isLinkHighlighted(link);
            if (highlighted) return true;
            if (interaction.hasHover && !highlighted) return false;
            return true;
          }}
          linkHoverPrecision={5}
          onNodeClick={handleNodeClick}
          onNodeHover={interaction.handleNodeHover}
          onLinkHover={interaction.handleLinkHover}
          onNodeDragEnd={handleNodeDragEnd}
          onZoom={adaptive.handleZoom}
          nodeLabel=""
          linkLabel=""
          warmupTicks={100}
          cooldownTicks={200}
          onEngineStop={handleEngineStop}
          d3AlphaDecay={0.05}
          d3VelocityDecay={0.4}
          enableNodeDrag={true}
          enableZoomInteraction={true}
          enablePanInteraction={true}
        />
      </div>

      <GraphTooltip
        visible={!!(interaction.hoveredNode || interaction.hoveredLink)}
        x={interaction.tooltipPos.x}
        y={interaction.tooltipPos.y}
      >
        {interaction.hoveredNode ? (
          <>
            <strong>{interaction.hoveredNode.name}</strong>
            {(interaction.hoveredNode.department || interaction.hoveredNode.projects_count != null) && (
              <>
                <br />
                <span className="text-[0.8em] opacity-90">
                  {interaction.hoveredNode.department && <>{interaction.hoveredNode.department}{interaction.hoveredNode.projects_count != null ? ' \u00b7 ' : ''}</>}
                  {interaction.hoveredNode.projects_count != null && (
                    <>{interaction.hoveredNode.projects_count} {t('faculty.outcomes')}</>
                  )}
                </span>
              </>
            )}
          </>
        ) : interaction.hoveredLink ? (
          <>
            {typeof interaction.hoveredLink.source === 'object' ? interaction.hoveredLink.source.name : interaction.hoveredLink.source}
            {` ${t('crossFaculty.and')} `}
            {typeof interaction.hoveredLink.target === 'object' ? interaction.hoveredLink.target.name : interaction.hoveredLink.target}
            <br />
            <span className="text-[0.8em] opacity-90">
              {interaction.hoveredLink.sharedProjects} {t('university.tooltip.sharedProjects')}
            </span>
          </>
        ) : null}
      </GraphTooltip>
    </div>
  );
}
